import { safeParseInt } from "encamp-shared/src/tierii/utilities";
import { KeyboardEvent, useCallback } from "react";

/**
 * A value some other source holds for the field being edited, offered to the
 * reviewer to take.
 *
 * "Other source" is whatever the surface has: e-Manifest and vendor data on a
 * manifest ingest, the record about to be written over on an extraction review.
 * The component neither knows nor cares which — it renders what it is given and
 * hands back the one that was taken.
 */
export interface OfferedValue<T> {
  /** Where it came from, as the reviewer reads it — "e-Manifest", "Vendor". */
  source: string;
  /** The value as it should read on screen, already formatted. */
  display: string;
  /** What to write into the field when this offer is taken. */
  value: T;
  /**
   * Something worth knowing about this value on hover — how sure the extractor
   * was of it, say.
   *
   * Only ever set on a value the thing being described actually produced. A
   * confidence attached to a value the model never read would describe the wrong
   * reading, and the reviewer has no way to tell.
   */
  tooltip?: string;
}

/**
 * The candidates worth offering: every source holding something, except the one
 * already in the field.
 *
 * A field has several sources — what the record holds, what the AI read, and on a
 * manifest what e-Manifest and the vendor say — and any of them can be the answer.
 * Which are worth offering depends only on what the input currently holds, so
 * taking one immediately offers back the value it replaced, and a reviewer can
 * move between them freely instead of choosing once and losing the alternatives.
 *
 * Filtering on the *current* value rather than on a flag is what makes that work.
 * A flag says "these two disagreed when we loaded"; it stops being true the moment
 * anything is taken, and with it the way back.
 *
 * A source holding nothing is not a candidate: offering a blank as a value to take
 * would read as an answer when it is an absence, and taking it would clear a field
 * the reviewer had filled.
 */
export function offersDifferingFrom<T>(
  candidates: readonly OfferedValue<T>[],
  isCurrent: (value: T) => boolean,
  isBlank: (value: T) => boolean
): OfferedValue<T>[] {
  return candidates.filter((candidate) => !isBlank(candidate.value) && !isCurrent(candidate.value));
}

/**
 * The keyboard shortcut for the nth offer, as the reviewer reads it.
 *
 * The first is Alt+Enter because it is the one they want almost every time —
 * accepting the only other reading of a field they cannot make out — and it needs
 * to be reachable without leaving the home row or counting chips. The rest are
 * numbered from two, matching their position in the row.
 */
export function shortcutLabel(index: number): string {
  return index === 0 ? "⌥↵" : `⌥${index + 1}`;
}

/**
 * Keeping what the field already holds.
 *
 * Outside the digit run on purpose. `⌥↵`, `⌥2`, `⌥3` all mean "replace this with
 * source n" — one action, numbered. Keeping is a different action, and giving it
 * a number would both muddle that and move it every time the number of offers
 * changes.
 */
export const KEEP_SHORTCUT_LABEL = "⌥K";

/**
 * Whether a keystroke means "keep what is here".
 *
 * Not Alt+Tab, which Windows and most Linux desktops swallow for window
 * switching before a browser ever sees it — a shortcut that works on one
 * platform is worse than one that works everywhere, because the badge advertises
 * it either way.
 *
 * The command palette is Cmd/Ctrl+K and requires one of those modifiers, so this
 * cannot reach it. The shared `K` is deliberate: Alt is already this row's
 * modifier — Alt+Enter, Alt+2, Alt+3 — so Alt+K reads as another member of that
 * family rather than as a near-miss for the palette.
 *
 * Matched on `code`, for the same reason the digits are: Alt+K on macOS reports
 * `key` as "˚".
 */
export function isKeepShortcut(event: KeyboardEvent): boolean {
  return event.altKey && !event.ctrlKey && !event.metaKey && event.code === "KeyK";
}

/**
 * Whether a keystroke asks for one of the offers, and which.
 *
 * Matched on `event.code`, not `event.key`: Alt with a digit on macOS produces a
 * typographic character rather than the digit (Alt+2 is "™"), so a `key`
 * comparison silently never fires on the platform most reviewers use. `code`
 * names the physical key and is unaffected.
 */
function offerIndexForEvent(event: KeyboardEvent, offerCount: number): number | null {
  if (!event.altKey || event.ctrlKey || event.metaKey) return null;
  if (event.code === "Enter" || event.code === "NumpadEnter") return offerCount > 0 ? 0 : null;
  const digit = /^Digit([1-9])$/.exec(event.code);
  if (digit == null) return null;
  const position = safeParseInt(digit[1]);
  if (position == null) return null;
  const index = position - 1;
  return index < offerCount ? index : null;
}

/**
 * Take an offered value if this keystroke asked for one, and say whether it did.
 *
 * The plain function rather than the hook, for a caller whose offers are only
 * known inside a render callback — react-hook-form's `Controller` computes the
 * value there, and a hook cannot be called from inside it. Returns false when the
 * keystroke meant something else, so the caller can go on handling it.
 */
export function takeOfferForEvent<T>(
  event: KeyboardEvent,
  offers: readonly OfferedValue<T>[],
  onTake: (offer: OfferedValue<T>) => void,
  onKeep?: () => void
): boolean {
  if (onKeep != null && isKeepShortcut(event)) {
    // Stopped like the others: Alt+K types "˚" into the field on macOS otherwise.
    event.preventDefault();
    event.stopPropagation();
    onKeep();
    return true;
  }

  const index = offerIndexForEvent(event, offers.length);
  if (index == null) return false;
  // Only once we know we are handling it: Alt+Enter and Alt+digit both mean
  // something to the browser and to assistive tech otherwise.
  event.preventDefault();
  event.stopPropagation();
  onTake(offers[index]);
  return true;
}

/**
 * Take an offered value from the keyboard, for a field control to spread onto
 * its input.
 *
 * Lives beside the chips rather than inside them because the keystroke happens
 * while the caret is in the input — the reviewer never focuses a chip to use one,
 * which is the whole point of offering values at the point of typing.
 */
export function useOfferedValueShortcuts<T>(
  offers: readonly OfferedValue<T>[],
  onTake: (offer: OfferedValue<T>) => void,
  onKeep?: () => void
): (event: KeyboardEvent) => void {
  return useCallback(
    (event: KeyboardEvent) => {
      takeOfferForEvent(event, offers, onTake, onKeep);
    },
    [offers, onTake, onKeep]
  );
}

/** An unfocused field's badge: unpainted, and the only thing giving up width. */
const HIDDEN_SHORTCUT = { visibility: "hidden", flexShrink: 1 } as const;
/**
 * An unfocused field's chip, which gives up nothing while its badge has width
 * left to give. Sharing the shrink at any ratio still took a fraction of a pixel
 * from the chip, and that is enough to push its source off the label.
 */
const HELD_CHIP = { flexShrink: 0 } as const;
/** The focused field's badge: painted, and holding its width. */
const SHOWN_SHORTCUT = { visibility: "visible", flexShrink: 0 } as const;
/** The focused field's chip, which gives way to its badge. */
const YIELDING_CHIP = { flexShrink: 1 } as const;

/**
 * The keystroke badges on a field's offered values, painted only while the caret
 * is in that field.
 *
 * Every field carries its own chips, but `⌥↵` only ever acts on the focused one —
 * so printing it on all of them advertises a keystroke that does nothing from
 * where the reviewer is standing. `:focus-within` rather than tracked state:
 * sixty fields re-rendering on every focus move to toggle a badge is a lot of
 * work for CSS that already knows the answer.
 *
 * `visibility`, not `display`. Removing the badge from the layout took its width
 * with it, so every chip on the row resized as the caret arrived and left and the
 * form reflowed under a reviewer on each tab. Instead an unfocused badge keeps
 * its place but yields its width first, so a value too long for a narrow column
 * reads into it; the focused one holds its width and the chip gives way. Each
 * chip and badge take the same width either way, so nothing moves.
 *
 * Two shapes because the surfaces nest differently. The extraction grid owns a
 * box per field and puts the rule there. The manifest form builds its inputs
 * through shared controls with no per-field wrapper to reach, so its rule goes on
 * the form container and scopes itself with MUI's own per-field root — the helper
 * slot the chips render into is inside that root, so `:focus-within` still means
 * "this field".
 */
export const REVEAL_SHORTCUTS_ON_FOCUS = {
  "& [data-offer-shortcut]": HIDDEN_SHORTCUT,
  "& [data-offer-chip]": HELD_CHIP,
  "&:focus-within [data-offer-shortcut]": SHOWN_SHORTCUT,
  "&:focus-within [data-offer-chip]": YIELDING_CHIP,
} as const;

/** The same rule, applied once to a form whose fields it cannot reach individually. */
export const REVEAL_SHORTCUTS_PER_FIELD = {
  "& .MuiFormControl-root [data-offer-shortcut]": HIDDEN_SHORTCUT,
  "& .MuiFormControl-root [data-offer-chip]": HELD_CHIP,
  "& .MuiFormControl-root:focus-within [data-offer-shortcut]": SHOWN_SHORTCUT,
  "& .MuiFormControl-root:focus-within [data-offer-chip]": YIELDING_CHIP,
} as const;
